import React, { useEffect, useState } from "react";
import { TrophyIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import ApiCustom from "../../utils/ApiCustom";
import clsx from "clsx";

const animation = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.4 } },
  exit: { opacity: 0, y: 10, transition: { duration: 0.2 } },
};

/**
 * Modal xem điểm KIỂU MỚI
 * - Lọc hội phí theo năm: nếu CHƯA đóng -> chỉ hiện thông báo
 * - Nếu ĐÃ đóng -> hiển thị Tổng điểm + Bảng tiêu chí (read-only) theo năm
 */
const MyPointDetailModal = ({
  open,
  onClose,
  companyId,
  companyName,
  year,
  onChangeYear,
  isUnpaid,
}) => {
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]); // all (auto + manual), sorted by order
  const [serverPoints, setServerPoints] = useState({}); // { [categoryId]: points } theo năm
  const [serverNotes, setServerNotes] = useState({}); // { [categoryId]: note } theo năm
  const [totalPoints, setTotalPoints] = useState(0);

  // Load categories
  const loadCategories = async () => {
    const res = await ApiCustom.get(`/point-categories`, {
      params: { page: 1, limit: 1000, s: "" },
    });
    const all = res.data?.data?.data || res.data?.data || [];
    const sorted =
      [...all].sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id - b.id) ||
      [];
    setCategories(sorted);
  };

  // Load partner points theo năm
  const loadPartnerPoints = async () => {
    try {
      const res = await ApiCustom.get(`/partner-points/${companyId}`, {
        params: { year },
      });
      const payload = res.data?.data || {};
      const items = payload.items || [];
      const pts = {};
      const notes = {};
      items.forEach((it) => {
        if (it?.pointCategoryId == null) return;
        pts[it.pointCategoryId] = Number(it.points || 0);
        if (it.note != null) notes[it.pointCategoryId] = String(it.note);
      });
      setServerPoints(pts);
      setServerNotes(notes);
      setTotalPoints(Number(payload.totalPoints || 0));
    } catch {
      setServerPoints({});
      setServerNotes({});
      setTotalPoints(0);
    }
  };

  useEffect(() => {
    if (!open || !companyId) return;
    if (isUnpaid) {
      setLoading(false); // chưa đóng: không tải chi tiết
      return;
    }
    (async () => {
      setLoading(true);
      try {
        await loadCategories();
        await loadPartnerPoints();
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, companyId, year, isUnpaid]);

  if (!open) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          {...animation}
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center"
          onClick={onClose}
        >
          <motion.div
            {...animation}
            className="relative bg-white w-full max-w-3xl mx-4 rounded-xl p-6 shadow-xl border border-amber-100 flex flex-col"
            style={{ maxHeight: "92vh" }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>

            <div className="mb-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <h2 className="text-xl font-bold mb-1">
                  Điểm hoạt động:{" "}
                  <span className="text-amber-700">{companyName || "—"}</span>
                </h2>
              </div>

              {/* Chọn năm trong modal */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Năm:</span>
                <select
                  className="select select-bordered select-sm"
                  value={year}
                  onChange={(e) => onChangeYear(Number(e.target.value))}
                >
                  {Array.from(
                    { length: 6 },
                    (_, i) => new Date().getFullYear() - i,
                  ).map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {isUnpaid ? (
              <div className="border rounded-lg p-4 bg-amber-50 text-amber-700">
                Hội viên <b>{companyName || ""}</b> chưa đóng hội phí năm {year}
                . Không có thống kê hiển thị.
              </div>
            ) : loading ? (
              <div className="text-center py-8 text-gray-400">Đang tải...</div>
            ) : (
              <>
                <div className="mb-4">
                  <div className="text-gray-600">Tổng điểm năm {year}:</div>
                  <div className="text-2xl font-bold text-amber-700">
                    {totalPoints}
                  </div>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto overflow-x-auto border rounded-lg">
                  <table className="table w-full">
                    <thead className="sticky top-0 bg-white">
                      <tr>
                        <th>Tiêu chí</th>
                        <th className="w-28 text-right">Điểm</th>
                        <th className="w-1/2">Ghi chú</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categories.map((c) => {
                        const cid = c.id;
                        const point = Number(serverPoints[cid] || 0);
                        const note = serverNotes[cid] ?? "";
                        return (
                          <tr key={cid}>
                            <td className="align-top">
                              <div className="font-medium">{c.name}</div>
                              <div className="text-xs text-gray-500">
                                Mã: {c.key}
                              </div>
                            </td>
                            <td className="align-top text-right font-semibold">
                              <span
                                className={clsx(
                                  point > 0
                                    ? "text-amber-700"
                                    : point < 0
                                      ? "text-gray-400"
                                      : "text-gray-600",
                                )}
                              >
                                {point}
                              </span>
                            </td>
                            <td className="align-top">
                              {note ? (
                                <div className="text-sm text-gray-700">
                                  {note}
                                </div>
                              ) : (
                                <span className="text-sm text-gray-400">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {categories.length === 0 && (
                        <tr>
                          <td
                            colSpan={3}
                            className="text-center text-gray-500 py-6"
                          >
                            Chưa có tiêu chí
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ======================= MAIN CARD (của chính mình) =======================
const MyActivityPointCard = () => {
  const [companyName, setCompanyName] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const companyId = localStorage.getItem("companyId");

  // Năm lọc (mặc định: năm hiện tại)
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);

  // Tổng điểm năm (của chính DN)
  const [totalYearPoint, setTotalYearPoint] = useState(0);

  // Trạng thái chưa đóng hội phí trong năm
  const [isUnpaid, setIsUnpaid] = useState(false);

  // Lấy thông tin DN để hiển thị tên
  const loadCompanyInfo = async () => {
    if (!companyId) {
      setCompanyName("");
      return;
    }
    try {
      const res = await ApiCustom.get(`/company-show-by-id/${companyId}`);
      setCompanyName(res?.data?.data?.companyName || "");
    } catch {
      setCompanyName("");
    }
  };

  // Kiểm tra hội phí năm (nếu chưa đóng -> isUnpaid = true)
  const checkUnpaidForYear = async (y) => {
    try {
      const r = await ApiCustom.get(`/membership-fee-shows`);
      const fees = r.data?.data?.data || [];

      const feesInYear = fees.filter((f) => {
        const t = new Date(f.createdAt || f.payStartDay || 0);
        return !isNaN(t) && t.getFullYear() === y;
      });

      if (!feesInYear.length) {
        // Không có đợt hội phí trong năm -> không chặn
        setIsUnpaid(false);
        return;
      }

      // Nếu DN nằm trong BẤT KỲ danh sách chưa đóng của năm => isUnpaid = true
      const lists = await Promise.all(
        feesInYear.map((f) =>
          ApiCustom.get(`/membership-fee-not-paid/${f.id}`)
            .then((res) => res.data?.data?.data?.companies || [])
            .catch(() => []),
        ),
      );
      const inAnyUnpaid =
        lists.flat().findIndex((c) => String(c?.id) === String(companyId)) >= 0;
      setIsUnpaid(inAnyUnpaid);
    } catch {
      setIsUnpaid(false);
    }
  };

  // Tải tổng điểm năm cho chính DN
  const loadTotalForYear = async (y) => {
    if (!companyId) {
      setTotalYearPoint(0);
      return;
    }
    try {
      const r = await ApiCustom.get(`/partner-points/${companyId}`, {
        params: { year: y },
      });
      const payload = r.data?.data || {};
      setTotalYearPoint(Number(payload.totalPoints || 0));
    } catch {
      setTotalYearPoint(0);
    }
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([loadCompanyInfo()]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    // Mỗi khi đổi năm -> kiểm tra hội phí + (nếu đã đóng) tải tổng điểm
    (async () => {
      await checkUnpaidForYear(year);
      await loadTotalForYear(year);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, companyId]);

  const displayPoint = loading ? "..." : isUnpaid ? 0 : totalYearPoint;

  return (
    <div className="w-full">
      {/* Card nhỏ hiển thị tổng điểm năm (mặc định năm hiện tại) */}
      <motion.div
        {...animation}
        whileHover={{ scale: 1.03 }}
        className="w-full sm:w-auto flex flex-col items-center text-center p-5 rounded-2xl border border-amber-100 bg-white transition-all cursor-pointer shadow-md "
        onClick={() => setModalOpen(true)}
      >
        <div className="p-3 bg-amber-100 rounded-full mb-3 flex items-center justify-center">
          <TrophyIcon className="h-6 w-6 text-amber-600" />
        </div>
        <p className="text-lg text-black mb-1">
          Điểm hoạt động của đơn vị bạn ({year})
        </p>
        <p
          className={clsx(
            "text-3xl font-bold",
            !loading && isUnpaid ? "text-gray-400" : "text-amber-700",
          )}
        >
          {displayPoint}
        </p>
        {/* {!loading && isUnpaid && (
          <p className="text-xs text-gray-500 mt-1">
            Chưa đóng hội phí năm {year}
          </p>
        )} */}
      </motion.div>

      {/* Modal chi tiết kiểu mới */}
      <MyPointDetailModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        companyId={companyId}
        companyName={companyName}
        year={year}
        onChangeYear={(y) => setYear(y)}
        isUnpaid={isUnpaid}
      />
    </div>
  );
};

export default MyActivityPointCard;
